import { useState, useContext, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Header } from '@trinity/chat-components/components/Modal/Header';
import { Modal } from '@trinity/chat-components/components/Modal/Modal';
import Button from '@trinity/chat-components/components/presentation/Button/Button';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context';
import { SigmaDashboardContext } from '@trinity/chat-components/contexts/DashboardContexts';
import styles from './styles/SigmaDashboardModal.module.css';

type Props = {
  open: boolean;
  onClose: () => void;
  /** Defaults to the create wording; an edit flow passes its own. */
  title?: string;
  /**
   * The values to open the form with. When omitted the form reads the existing dashboard from
   * SigmaDashboardContext, as apps/frontend's project and brand providers supply it.
   */
  initialValues?: Partial<SigmaDashboardFormValues>;
  updateSigmaDashboard: (data: {
    warehouse: string;
    schema: string;
    analysisId?: string | null;
    tabName: string;
    includeTreatmentClaimCount?: boolean;
    includeEpidemiology?: boolean;
    includeLineOfTherapy?: boolean;
    includeCompliance?: boolean;
    includePersistence?: boolean;
  }) => Promise<void>;
};

export type SigmaDashboardFormValues = FormState;

type FormState = {
  warehouse: string;
  schema: string;
  analysisId: string;
  tabName: string;
  includeTreatmentClaimCount: boolean;
  includeEpidemiology: boolean;
  includeLineOfTherapy: boolean;
  includeCompliance: boolean;
  includePersistence: boolean;
};

function getFormStateFromContext(ctx: typeof import('@trinity/chat-components/contexts/DashboardContexts').SigmaDashboardContext extends React.Context<infer T> ? T : never): FormState {
  if (!ctx) {
    return { warehouse: '', schema: '', analysisId: '', tabName: '', includeTreatmentClaimCount: false, includeEpidemiology: false, includeLineOfTherapy: false, includeCompliance: false, includePersistence: false };
  }
  return {
    warehouse: ctx.warehouse || '',
    schema: ctx.schema || '',
    analysisId: ctx.analysisId || '',
    tabName: ctx.tabName || '',
    includeTreatmentClaimCount: ctx.includeTreatmentClaimCount || false,
    includeEpidemiology: ctx.includeEpidemiology || false,
    includeLineOfTherapy: ctx.includeLineOfTherapy || false,
    includeCompliance: ctx.includeCompliance || false,
    includePersistence: ctx.includePersistence || false,
  };
}

function getThemedButtonStyle(type: 'cancel' | 'continue', mainColor: string, bubbleColor: string, disabled = false) {
  if (type === 'cancel') {
    return {
      background: bubbleColor,
      color: '#64748b',
      fontWeight: 500,
      borderRadius: 6,
      padding: '8px 20px',
      border: 'none',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'background 0.15s, color 0.15s',
    };
  }
  return {
    background: disabled ? '#d1d5db' : mainColor,
    color: disabled ? '#6b7280' : '#fff',
    fontWeight: 500,
    borderRadius: 6,
    padding: '8px 20px',
    border: 'none',
    fontSize: '1rem',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background 0.15s, color 0.15s',
  };
}

function CheckboxField({ checked, onChange, disabled, label }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string }) {
  return (
    <div className={styles.label} style={{ marginTop: 12 }}>
      <label style={{ color: disabled ? '#9ca3af' : 'inherit' }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={e => onChange(e.target.checked)}
          disabled={disabled}
          style={{ marginRight: 8 }}
        />
        {label}
      </label>
    </div>
  );
}

function ModalFooter({ isThemingEnabled, getButtonStyle, onClose, allFilled, pfaValid, isSubmitting, handleContinue }: {
  isThemingEnabled: boolean | undefined;
  getButtonStyle: (type: 'cancel' | 'continue', disabled?: boolean) => React.CSSProperties | undefined;
  onClose: () => void;
  allFilled: boolean;
  pfaValid: boolean;
  isSubmitting: boolean;
  handleContinue: () => void;
}) {
  return (
    <>
      <Button
        className={styles.cancel}
        variant="brick"
        color="secondary"
        onClick={onClose}
        style={isThemingEnabled ? getButtonStyle('cancel') : undefined}>
        Cancel
      </Button>
      <Button
        className={styles.continue}
        variant="brick"
        color="primary"
        disabled={!allFilled || !pfaValid || isSubmitting}
        onClick={handleContinue}
        style={isThemingEnabled ? getButtonStyle('continue', !allFilled || !pfaValid || isSubmitting) : undefined}>
        Continue
      </Button>
    </>
  );
}

function computeFormValidity(warehouse: string, schema: string, includeLineOfTherapy: boolean, includeCompliance: boolean, includePersistence: boolean, analysisId: string) {
  const allFilled = (warehouse || '').trim() && (schema || '').trim();
  const pfaFieldsSelected = includeLineOfTherapy || includeCompliance || includePersistence;
  const pfaValid = !pfaFieldsSelected || (analysisId || '').trim();
  return { allFilled: !!allFilled, pfaValid: !!pfaValid };
}

function useThemingStyles(theming: { enabled?: boolean; palette?: { primary?: { main?: string }; prompt?: { main?: string } } } | null) {
  const isThemingEnabled = theming?.enabled;
  const mainColor = theming?.palette?.primary?.main || '#2563eb';
  const bubbleColor = theming?.palette?.prompt?.main || '#e0e7ef';

  const getButtonStyle = (type: 'cancel' | 'continue', disabled = false) => {
    if (!isThemingEnabled) return undefined;
    return getThemedButtonStyle(type, mainColor, bubbleColor, disabled);
  };

  return { isThemingEnabled, getButtonStyle };
}

export const SigmaDashboardModal = ({
  open,
  onClose,
  title = 'Enter Dashboard Connection Details',
  initialValues,
  updateSigmaDashboard,
}: Props) => {
  const existingSigmaDashboard = useContext(SigmaDashboardContext);

  const [warehouse, setWarehouse] = useState('');
  const [schema, setSchema] = useState('');
  const [analysisId, setAnalysisId] = useState('');
  const [tabName, setTabName] = useState('');
  const [includeTreatmentClaimCount, setIncludeTreatmentClaimCount] = useState(false);
  const [includeEpidemiology, setIncludeEpidemiology] = useState(false);
  const [includeLineOfTherapy, setIncludeLineOfTherapy] = useState(false);
  const [includeCompliance, setIncludeCompliance] = useState(false);
  const [includePersistence, setIncludePersistence] = useState(false);

  const analysisIdProvided = analysisId.trim().length > 0;

  useEffect(() => {
    if (open) {
      const state = initialValues
        ? { ...getFormStateFromContext(null), ...initialValues }
        : getFormStateFromContext(existingSigmaDashboard);
      setWarehouse(state.warehouse);
      setSchema(state.schema);
      setAnalysisId(state.analysisId);
      setTabName(state.tabName);
      setIncludeTreatmentClaimCount(state.includeTreatmentClaimCount);
      setIncludeEpidemiology(state.includeEpidemiology);
      setIncludeLineOfTherapy(state.includeLineOfTherapy);
      setIncludeCompliance(state.includeCompliance);
      setIncludePersistence(state.includePersistence);
    }
  }, [open, existingSigmaDashboard, initialValues]);

  const { allFilled: allFilledBool, pfaValid: pfaValidBool } = computeFormValidity(warehouse, schema, includeLineOfTherapy, includeCompliance, includePersistence, analysisId);

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: () => {
      const finalTabName = (tabName || '').trim() || 'Sigma Dashboard';
      return updateSigmaDashboard({
        warehouse,
        schema,
        analysisId,
        tabName: finalTabName,
        includeTreatmentClaimCount,
        includeEpidemiology,
        includeLineOfTherapy,
        includeCompliance,
        includePersistence,
      });
    },
    onSuccess: () => {
      onClose();
    },
  });

  const theming = useContext(AppThemingContext);
  const { isThemingEnabled, getButtonStyle } = useThemingStyles(theming);

  const handleContinue = async () => {
    if (allFilledBool && pfaValidBool) {
      await mutateAsync();
    }
  };

  return (
    <Modal open={open} onClose={onClose} className={styles.modal}>
      <Header text={title} />
      <div className={styles.body}>
        <div className={styles.inputsContainer}>
          <div className={styles.label}>Warehouse ID</div>
          <input
            className={styles.input}
            placeholder="Warehouse"
            value={warehouse}
            onChange={e => setWarehouse(e.target.value)}
          />
          <div className={styles.label} style={{ marginTop: 24 }}>
            Schema ID
          </div>
          <input
            className={styles.input}
            placeholder="Schema"
            value={schema}
            onChange={e => setSchema(e.target.value)}
          />
          <div className={styles.label} style={{ marginTop: 24 }}>
            Analysis ID{' '}
            <span style={{ fontSize: '0.9em', color: '#64748b' }}>
              (only needed for Patient Flow Analyzer Dashboard)
            </span>
          </div>
          <input
            className={styles.input}
            placeholder="Analysis ID"
            value={analysisId}
            onChange={e => setAnalysisId(e.target.value)}
          />
          <div className={styles.label} style={{ marginTop: 24 }}>
            Dashboard Tab Name
          </div>
          <input
            className={styles.input}
            placeholder="Dashboard Tab Name"
            value={tabName}
            onChange={e => setTabName(e.target.value)}
          />
          <div className={styles.label} style={{ marginTop: 24 }}>
            <label>
              <input
                type="checkbox"
                checked={includeTreatmentClaimCount}
                onChange={e => setIncludeTreatmentClaimCount(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              Include Treatment Claim Count
            </label>
          </div>
          <CheckboxField checked={includeEpidemiology} onChange={setIncludeEpidemiology} label="Include Epidemiology" />
          <CheckboxField checked={includeLineOfTherapy} onChange={setIncludeLineOfTherapy} disabled={!analysisIdProvided} label="Include Line of Therapy" />
          <CheckboxField checked={includeCompliance} onChange={setIncludeCompliance} disabled={!analysisIdProvided} label="Include Compliance" />
          <CheckboxField checked={includePersistence} onChange={setIncludePersistence} disabled={!analysisIdProvided} label="Include Persistence" />
        </div>
      </div>
      <div className={styles.footer}>
        <ModalFooter
          isThemingEnabled={isThemingEnabled}
          getButtonStyle={getButtonStyle}
          onClose={onClose}
          allFilled={allFilledBool}
          pfaValid={pfaValidBool}
          isSubmitting={isSubmitting}
          handleContinue={handleContinue}
        />
      </div>
    </Modal>
  );
};
